// frontend/src/components/pages/AutoTradingStatsPanel.js
import { formatExitReason } from '../../utils/exitReasonLabel';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  sortTradesByExitTime,
  remainingAfterExit,
  closedTradeCycles,
  countEntries,
} from '../../utils/autoTradingExits';

/**
 * - latestClose는 부모에서만 전달 (fetch X)
 * - /auto/strategies/history : 최초 1회 로드 (+ 수동 새로고침 버튼)
 * - /auto/strategies/active  : hasLive 기준 1초/60초 폴링
 * - LIVE 포지션 강도(cum/max/min): entry_timestamp ~ 현재까지 ls_nasdaq_ohlcp.power 누적합 계산 (1초 갱신)
 * - 모든 취소/Abort 예외는 무시, 언핸들드 리젝션 없음
 */
export default function AutoTradingStatsPanel({
  latestClose,
  className = '',
  historyMonths = 12,
  pollMsActive = 1000,
  pollMsInactive = 5000,
  pollMsHistory = 5000, // 히스토리 자동 갱신 주기
  pollMsHistoryLive = 5000, // LIVE 중 history 전체 polling 최소 주기
  pollMsPower = 5000,
}) {
  const EXIT_REASON_LABELS = {
    stop_loss: '손절',
    break_even: '본절 청산',
    take_profit_30: '30p 분할 익절',
    take_profit_45: '45p 분할 익절',
    power_50: '강도 50% 익절',
    power_zero: '강도 0 익절',
    market_end: '장마감 전 청산',
    risk_block: '손실 한도 초과',
    power_80: '강도 80% 익절',
    power_85: '강도 85% 익절',
    power_90: '강도 90% 익절',
    premarket_profit_over_100p: '프리장 100p 수익 익절',
    price_over_25p_during_auto_trading_blocked: '25p이탈 진입차단',
  };

  const API = process.env.REACT_APP_API_URL;
  const MONTHS = process.env.REACT_APP_HISTORY_RANGE_MONTHS;

  // 서머타임 여부 확인: true (CDT, Daylight Time(서머타임)), false (CST, Standard Time)
  const IS_DST = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    timeZoneName: 'short',
  })
    .formatToParts(new Date())
    .some((p) => p.value === 'CDT');

  const [history, setHistory] = useState([]);
  const [active, setActive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const historyFpRef = useRef(''); // 히스토리 변경 감지용 지문

  // LIVE 강도 상태: { [strategyId]: { entry_timestamp, power_start_ts, startMs, cum, max, min } }
  const [livePowerStats, setLivePowerStats] = useState({});
  const livePowerStatsRef = useRef({});
  const livePowerMetaRef = useRef({
    lastOhlcpTs: null,
    lastSyncAt: null,
    activeFp: '',
  });

  useEffect(() => {
    livePowerStatsRef.current = livePowerStats;
  }, [livePowerStats]);

  // 진행중 요청 취소용
  const inFlight = useRef({
    active: null,
    history: null,
    historyPoll: null,
    livePower: null,
    ohlcpLatest: null,
  });
  const cancel = (key) => {
    try {
      inFlight.current[key]?.abort?.();
    } catch {
      /* noop */
    }
    inFlight.current[key] = null;
  };

  // ─── UTILS ───────────────────────────────

  function mapExitReason(reason, event) {
    return formatExitReason(reason, event, EXIT_REASON_LABELS);
  }

  // axios 취소/Abort를 항상 무시하도록 통일
  function isCanceled(err) {
    return (
      err?.code === 'ERR_CANCELED' ||
      err?.name === 'CanceledError' ||
      (typeof axios.isCancel === 'function' && axios.isCancel(err))
    );
  }

  // 세션 경계(07:00 ~ 익일 06:00) 계산 유틸
  function getCurrentSessionWindowKST() {
    // KST '지금'
    const nowLocal = new Date();
    const nowKST = new Date(
      nowLocal.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }),
    );

    // 세션 시작
    // 서머타임 - 07시 이후면 오늘 07:00, 아니면 어제 07:00
    // 비 서머타임 - 08시 이후면 오늘 08:00, 아니면 어제 08:00
    let start = new Date(nowKST);

    if (IS_DST) {
      if (nowKST.getHours() >= 7) {
        start.setHours(7, 0, 0, 0);
      } else {
        start.setDate(start.getDate() - 1);
        start.setHours(7, 0, 0, 0);
      }
    } else {
      if (nowKST.getHours() >= 8) {
        start.setHours(8, 0, 0, 0);
      } else {
        start.setDate(start.getDate() - 1);
        start.setHours(8, 0, 0, 0);
      }
    }

    // 세션 종료: 시작 + 23시간 = 익일 06:00
    const end = new Date(start.getTime() + 23 * 60 * 60 * 1000);

    // ISO(+09:00) 문자열로 변환 (백엔드가 tz-aware를 기대)
    const toKstIso = (d) => {
      const pad = (n) => String(n).padStart(2, '0');
      const y = d.getFullYear();
      const m = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      const hh = pad(d.getHours());
      const mm = pad(d.getMinutes());
      const ss = pad(d.getSeconds());
      return `${y}-${m}-${day}T${hh}:${mm}:${ss}+09:00`;
    };

    return {
      startKst: start,
      endKst: end,
      startIsoKst: toKstIso(start),
      endIsoKst: toKstIso(end),
      sessionLabel: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(
        start.getDate(),
      ).padStart(2, '0')}`, // 라벨(YYYY-MM-DD) = 세션 날짜
    };
  }

  function isBreakEvenExit(reason) {
    if (!reason) return false;
    // JSONB가 객체로 올 가능성 방어: {type: 'break_even', ...} 등
    if (typeof reason === 'object') {
      const code = reason.type || reason.reason || reason.code;
      return code === 'break_even';
    }
    return reason === 'break_even';
  }

  // ─── 표시용 유틸(KST) ───────────────────────────────
  const fmtKst = (isoOrDate) => {
    if (!isoOrDate) return '';
    const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
    return d.toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  // 천단위 구분 + 소수 둘째자리까지 표시
  const fmtPrice = (n) =>
    n == null
      ? ''
      : n.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  // 강도 표시(정수)
  const fmtPower = (n) =>
    Number.isFinite(Number(n))
      ? Math.trunc(Number(n)).toLocaleString('en-US')
      : '—';

  // 포지션 유지 시간 포맷터(d일 hh:mm:ss)
  function fmtDuration(ms) {
    if (!Number.isFinite(ms) || ms < 0) return '—';
    const sec = Math.floor(ms / 1000);
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    const pad = (n) => String(n).padStart(2, '0');

    return (d > 0 ? `${d}일 ` : '') + `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  // 캘린더(day/week/month/3months) 범위
  function getKstRanges(now = new Date()) {
    const toKst = (d) =>
      new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const k = toKst(now);
    const y = k.getFullYear(),
      m = k.getMonth(),
      dd = k.getDate();

    const dayStart = new Date(y, m, dd, 0, 0, 0);
    const dayEnd = new Date(y, m, dd, 23, 59, 59, 999);

    const weekStart = new Date(dayStart);
    const w = (k.getDay() + 6) % 7; // 월=0
    weekStart.setDate(weekStart.getDate() - w);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    weekEnd.setMilliseconds(-1);

    const monthStart = new Date(y, m, 1, 0, 0, 0);
    const monthEnd = new Date(y, m + 1, 1, 0, 0, 0);
    monthEnd.setMilliseconds(-1);

    const threeMonthStart = new Date(y, m - 2, 1, 0, 0, 0);
    const threeMonthEnd = new Date(monthEnd);

    const sixMonthStart = new Date(y, m - 5, 1, 0, 0, 0);
    const sixMonthEnd = new Date(monthEnd);

    const twelveMonthStart = new Date(y, m - 11, 1, 0, 0, 0);
    const twelveMonthEnd = new Date(monthEnd);

    return {
      day: { start: dayStart, end: dayEnd },
      week: { start: weekStart, end: weekEnd },
      month: { start: monthStart, end: monthEnd },
      threeMonths: { start: threeMonthStart, end: threeMonthEnd },
      sixMonths: { start: sixMonthStart, end: sixMonthEnd },
      twelveMonths: { start: twelveMonthStart, end: twelveMonthEnd },
    };
  }

  const ranges = useMemo(() => getKstRanges(new Date()), []);

  //** 숫자 표시 Helper */
  const toFiniteNumber = (value, fallback = null) => {
    if (value == null || value === '') return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  const fmtQty = (value) => {
    const n = toFiniteNumber(value, 0);
    return Number.isFinite(n) ? String(Math.trunc(n)) : '0';
  };

  const pickFirstNumber = (...values) => {
    for (const v of values) {
      const n = toFiniteNumber(v);
      if (n != null) return n;
    }
    return null;
  };

  const normalizeFills = (fills) => {
    if (!Array.isArray(fills)) return [];
    return fills
      .map((f) => ({
        exec_no: f.exec_no ?? f.broker_exec_no ?? f.OvrsFutsExecNo ?? '',
        qty: toFiniteNumber(f.qty ?? f.fill_qty ?? f.ExecQty, 0),
        price: toFiniteNumber(f.price ?? f.fill_price ?? f.AbrdFutsExecPrc),
        t: f.t ?? f.fill_time ?? f.ExecDttm ?? null,
      }))
      .filter((f) => f.qty > 0 && f.price != null);
  };

  const weightedAvgFromFills = (fills) => {
    const normalized = normalizeFills(fills);
    const qty = normalized.reduce((acc, f) => acc + f.qty, 0);
    if (qty <= 0) return null;
    return normalized.reduce((acc, f) => acc + f.price * f.qty, 0) / qty;
  };

  const decisionEntryPriceFromFills = (directionLong, fills) => {
    const prices = normalizeFills(fills).map((f) => f.price);
    if (!prices.length) return null;
    return directionLong ? Math.min(...prices) : Math.max(...prices);
  };

  // ─── API ────────────────────────────────────────────
  async function fetchActive() {
    cancel('active');
    const ctrl = new AbortController();
    inFlight.current.active = ctrl;
    try {
      const res = await axios.get(`${API}/auto-trading/strategies/active`, {
        signal: ctrl.signal,
      });
      return res.data ?? [];
    } catch (e) {
      if (isCanceled(e)) return null; // 취소면 아무 것도 업데이트하지 않음
      throw e;
    } finally {
      inFlight.current.active = null;
    }
  }

  async function fetchHistory() {
    cancel('history');
    const ctrl = new AbortController();
    inFlight.current.history = ctrl;
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/auto-trading/strategies/history`, {
        params: { months: MONTHS },
        signal: ctrl.signal,
      });
      return res.data ?? [];
    } catch (e) {
      if (isCanceled(e)) return null;
      throw e;
    } finally {
      inFlight.current.history = null;
      setLoadingHistory(false);
    }
  }

  // 히스토리 자동 폴링(로딩 스피너 없이)
  async function fetchHistorySilent() {
    cancel('historyPoll');
    const ctrl = new AbortController();
    inFlight.current.historyPoll = ctrl;
    try {
      const res = await axios.get(`${API}/auto-trading/strategies/history`, {
        params: { months: MONTHS },
        signal: ctrl.signal,
      });
      return res.data ?? [];
    } catch (e) {
      if (isCanceled(e)) return null;
      throw e;
    } finally {
      inFlight.current.historyPoll = null;
    }
  }

  // LIVE 강도 계산용
  async function fetchLivePower() {
    cancel('livePower');
    const ctrl = new AbortController();
    inFlight.current.livePower = ctrl;
    try {
      const res = await axios.get(`${API}/auto-trading/strategies/live-power`, {
        signal: ctrl.signal,
      });
      return res.data ?? null;
    } catch (e) {
      if (isCanceled(e)) return null;
      throw e;
    } finally {
      inFlight.current.livePower = null;
    }
  }

  async function fetchOhlcpLatestSince(sinceIso) {
    cancel('ohlcpLatest');
    const ctrl = new AbortController();
    inFlight.current.ohlcpLatest = ctrl;
    try {
      const res = await axios.get(`${API}/data/ls_nasdaq_ohlcp/latest`, {
        params: sinceIso ? { since: sinceIso } : undefined,
        signal: ctrl.signal,
      });
      const data = res.data ?? [];
      return Array.isArray(data) ? data : [data];
    } catch (e) {
      if (isCanceled(e)) return null;
      throw e;
    } finally {
      inFlight.current.ohlcpLatest = null;
    }
  }

  // 히스토리 지문(Fingerprint): 최신 이벤트 시각 + 이벤트 총수 + 전략 수
  function calcHistoryFingerprint(list) {
    let latestTs = 0;
    let eventsCount = 0;
    const len = Array.isArray(list) ? list.length : 0;

    for (const s of list || []) {
      const evts = Array.isArray(s.events_history) ? s.events_history : [];
      eventsCount += evts.length;
      for (const e of evts) {
        if (!e?.t) continue;
        const t = new Date(e.t).getTime();
        if (Number.isFinite(t) && t > latestTs) latestTs = t;
      }
    }
    return `${len}|${eventsCount}|${latestTs}`;
  }

  // 최초 로드: allSettled로 언핸들드 리젝션 방지
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const [h, a] = await Promise.allSettled([fetchHistory(), fetchActive()]);
      if (!mounted) return;
      if (h.status === 'fulfilled' && h.value) {
        setHistory(h.value);
        historyFpRef.current = calcHistoryFingerprint(h.value);
      }
      if (a.status === 'fulfilled' && a.value) setActive(a.value);
      setLoading(false);
    })().catch(() => {
      /* 안전장치 – 여기까지 오면 무시 */
    });
    return () => {
      mounted = false;
      cancel('active');
      cancel('history');
      cancel('historyPoll');
      cancel('livePower');
      cancel('ohlcpLatest');
    };
  }, [historyMonths]);

  const hasLive = useMemo(() => active?.some((s) => s.is_enter), [active]);

  // 활성 포지션만 폴링 (취소 예외 무시)
  useEffect(() => {
    let timer = null;
    const tick = async () => {
      try {
        const a = await fetchActive();
        if (a) setActive(a); // 취소(null)면 갱신 생략
      } catch (e) {
        if (!isCanceled(e)) console.error(e);
      }
    };
    timer = setInterval(tick, hasLive ? pollMsActive : pollMsInactive);
    return () => clearInterval(timer);
  }, [hasLive, pollMsActive, pollMsInactive]);

  // 히스토리 자동 반영
  // 히스토리 자동 반영
  useEffect(() => {
    let timer = null;

    const tick = async () => {
      try {
        const newHist = await fetchHistorySilent();
        if (!newHist) return;

        const newFp = calcHistoryFingerprint(newHist);

        // LIVE 중에도 history 전체를 무조건 setState하지 않는다.
        // 이벤트 수나 최신 이벤트 시각이 바뀐 경우에만 반영한다.
        if (newFp !== historyFpRef.current) {
          setHistory(newHist);
          historyFpRef.current = newFp;
        }
      } catch (e) {
        if (!isCanceled(e)) console.error(e);
      }
    };

    // 핵심:
    // LIVE 중에도 pollMsActive(기본 1초)를 따라가지 않게 한다.
    // /history?days=365 전체 조회는 최소 pollMsHistoryLive 이상으로 제한한다.
    const historyInterval = hasLive
      ? Math.max(pollMsHistoryLive, pollMsHistory)
      : pollMsHistory;

    tick();
    timer = setInterval(tick, historyInterval);

    return () => {
      clearInterval(timer);
      cancel('historyPoll');
    };
  }, [historyMonths, pollMsHistory, pollMsHistoryLive, hasLive, API]);

  // ─────────────────────────────────────────────────────────────
  // LIVE 포지션 누적강도/최대/최저 계산
  // 기준 시점: /strategies/active의 entry_timestamp
  // 데이터: /data/ls_nasdaq_ohlcp/latest?since=...
  // ─────────────────────────────────────────────────────────────

  const floorSecondMs = (value) => {
    const ms = new Date(value).getTime();
    if (!Number.isFinite(ms)) return NaN;
    return Math.floor(ms / 1000) * 1000;
  };

  const liveActiveInfos = useMemo(() => {
    if (!Array.isArray(active)) return [];
    return active
      .filter((s) => s.is_enter)
      .map((s) => ({
        id: s.id,
        entry_timestamp: s.entry_timestamp ?? null,
        startMs: floorSecondMs(s.entry_timestamp),
      }))
      .filter((x) => Number.isFinite(x.startMs));
  }, [active]);

  const liveActiveFp = useMemo(
    () =>
      liveActiveInfos
        .map((x) => `${x.id}:${x.entry_timestamp ?? ''}`)
        .join('|'),
    [liveActiveInfos],
  );

  // livePowerStats 초기화/정리 (새 진입/종료 감지)
  useEffect(() => {
    setLivePowerStats((prev) => {
      const next = { ...prev };
      const liveIdSet = new Set(liveActiveInfos.map((x) => x.id));

      for (const key of Object.keys(next)) {
        const id = Number(key);
        if (!liveIdSet.has(id)) delete next[key];
      }

      for (const info of liveActiveInfos) {
        const cur = next[String(info.id)];
        if (!cur || cur.entry_timestamp !== info.entry_timestamp) {
          next[String(info.id)] = {
            entry_timestamp: info.entry_timestamp,
            power_start_ts: new Date(info.startMs).toISOString(),
            startMs: info.startMs,
            cum: 0,
            max: 0,
            min: 0,
          };
        }
      }

      return next;
    });

    livePowerMetaRef.current = {
      lastOhlcpTs: null,
      lastSyncAt: null,
      activeFp: liveActiveFp,
    };
  }, [liveActiveFp, liveActiveInfos]);

  // 서버 기준 동기화
  useEffect(() => {
    if (!hasLive) {
      setLivePowerStats({});
      livePowerMetaRef.current = {
        lastOhlcpTs: null,
        lastSyncAt: null,
        activeFp: '',
      };
      return;
    }

    let timer = null;
    let mounted = true;

    const syncNow = async () => {
      try {
        const payload = await fetchLivePower();
        if (!mounted || !payload) return;

        const items = Array.isArray(payload.items) ? payload.items : [];
        const mapped = {};

        for (const item of items) {
          const id = item?.strategy_id;
          if (id == null) continue;

          const powerStartTs =
            item?.power_start_ts ?? item?.entry_timestamp ?? null;
          const startMs = powerStartTs ? floorSecondMs(powerStartTs) : NaN;

          mapped[String(id)] = {
            entry_timestamp: item?.entry_timestamp ?? null,
            power_start_ts: powerStartTs,
            startMs,
            cum: Number(item?.cum_power) || 0,
            max: Number(item?.max_power) || 0,
            min: Number(item?.min_power) || 0,
          };
        }

        setLivePowerStats(mapped);
        livePowerMetaRef.current = {
          lastOhlcpTs: payload?.last_ohlcp_timestamp ?? null,
          lastSyncAt: payload?.server_time ?? new Date().toISOString(),
          activeFp: liveActiveFp,
        };
      } catch (e) {
        if (!isCanceled(e)) console.error(e);
      }
    };

    syncNow(); // 새 포지션 발생 즉시 1회
    timer = setInterval(syncNow, pollMsPower);

    return () => {
      mounted = false;
      clearInterval(timer);
      cancel('livePower');
    };
  }, [hasLive, liveActiveFp, pollMsPower, API]);

  // 1초 증분 반영
  useEffect(() => {
    if (!hasLive) return;

    let timer = null;
    let mounted = true;

    const tick = async () => {
      try {
        const meta = livePowerMetaRef.current || {};
        const sinceIso = meta.lastOhlcpTs;
        if (!sinceIso) return;

        const rows = await fetchOhlcpLatestSince(sinceIso);
        if (!mounted || !rows || !Array.isArray(rows) || rows.length === 0)
          return;

        const parsed = rows
          .map((r) => {
            const iso = r?.timestamp_korean ?? null;
            const tMs = iso ? new Date(iso).getTime() : NaN;
            const p = Number(r?.power) || 0;
            return { iso, tMs, p };
          })
          .filter((x) => x.iso && Number.isFinite(x.tMs))
          .sort((a, b) => a.tMs - b.tMs);

        if (!parsed.length) return;

        const lastIso = parsed[parsed.length - 1].iso;

        setLivePowerStats((prev) => {
          const next = { ...prev };

          for (const info of liveActiveInfos) {
            const key = String(info.id);
            const base = next[key];
            if (!base) continue;

            let cum = Number(base.cum) || 0;
            let max = Number.isFinite(base.max) ? Number(base.max) : cum;
            let min = Number.isFinite(base.min) ? Number(base.min) : cum;
            const startMs = Number.isFinite(base.startMs)
              ? base.startMs
              : info.startMs;

            for (const rec of parsed) {
              if (rec.tMs < startMs) continue;
              cum += rec.p;
              if (cum > max) max = cum;
              if (cum < min) min = cum;
            }

            next[key] = {
              ...base,
              startMs,
              cum,
              max,
              min,
            };
          }

          return next;
        });

        livePowerMetaRef.current = {
          ...(livePowerMetaRef.current || {}),
          lastOhlcpTs: lastIso ?? meta.lastOhlcpTs ?? null,
        };
      } catch (e) {
        if (!isCanceled(e)) console.error(e);
      }
    };

    timer = setInterval(tick, 1000);

    return () => {
      mounted = false;
      clearInterval(timer);
      cancel('ohlcpLatest');
    };
  }, [hasLive, liveActiveFp, API]);
  function eventTimeFromFillsOrEvent(e) {
    const fills = normalizeFills(e?.fills);
    const fillTimes = fills
      .map((f) => f.t)
      .filter(Boolean)
      .map((t) => new Date(t))
      .filter((d) => !Number.isNaN(d.getTime()));

    if (fillTimes.length > 0) {
      return new Date(
        Math.min(...fillTimes.map((d) => d.getTime())),
      ).toISOString();
    }

    return e?.t ?? null;
  }

  function eventQtyFromFillsOrEvent(e, fallback = 1) {
    const fills = normalizeFills(e?.fills);
    const fillQty = fills.reduce((acc, f) => acc + Number(f.qty || 0), 0);
    if (fillQty > 0) return fillQty;

    return toFiniteNumber(
      e?.qty ?? e?.open_qty ?? e?.entry_filled_qty,
      fallback,
    );
  }

  function eventAvgPriceFromFillsOrEvent(e) {
    const fills = normalizeFills(e?.fills);
    return pickFirstNumber(
      weightedAvgFromFills(fills),
      e?.avg_entry_price,
      e?.avg_exit_price,
      e?.price,
    );
  }

  function makeTradeKey(strategyId, enterEvent, index) {
    return (
      enterEvent?.trade_key ||
      `${strategyId}-${enterEvent?.entry_order_id || enterEvent?.broker_order_no || index}`
    );
  }

  function exitTimeFromFillsOrEvent(e) {
    const fills = normalizeFills(e?.fills);
    const fillTimes = fills
      .map((f) => f.t)
      .filter(Boolean)
      .map((t) => new Date(t))
      .filter((d) => !Number.isNaN(d.getTime()));

    if (fillTimes.length > 0) {
      return new Date(
        Math.max(...fillTimes.map((d) => d.getTime())),
      ).toISOString();
    }

    return e?.t ?? null;
  }

  // ─── 이벤트 -> 거래 리스트 복원 ─────────────────────
  function buildTradesFromEvents(strategies, _latestClose, activeList) {
    const latest = typeof _latestClose === 'number' ? _latestClose : null;
    const activeSet = new Set(
      (activeList || []).filter((s) => s.is_enter).map((s) => s.id),
    );
    const activeMap = new Map((activeList || []).map((s) => [s.id, s]));
    const trades = [];

    for (const s of strategies) {
      const activeRow = activeMap.get(s.id);
      const directionLong = s.direction === 'long' || s.direction === true;
      const directionLabel = directionLong ? '매수' : '매도';
      const stop_loss_price = Number(s.stop_loss_price);
      const trailing_stop_price =
        s.trailing_stop_price != null ? Number(s.trailing_stop_price) : 0;

      const evts = Array.isArray(s.events_history) ? [...s.events_history] : [];

      let open = null;
      let enterIndex = 0;

      for (const e of evts) {
        if (e.type === 'enter') {
          enterIndex += 1;

          // 기존 open이 닫히지 않은 상태에서 새 enter가 오면
          // 이벤트 기록이 꼬인 것으로 보고 새 회차를 분리한다.
          // 단, closed trade로 만들지는 않고 마지막 live 후보만 유지한다.
          const entryFills = normalizeFills(e.fills);
          const entryTime = eventTimeFromFillsOrEvent(e) || e.t;

          const decisionEntryPrice = pickFirstNumber(
            e.decision_entry_price,
            e.real_entry_price,
            e.entry_price,
            e.price,
            decisionEntryPriceFromFills(directionLong, entryFills),
          );

          const avgEntryPrice = pickFirstNumber(
            weightedAvgFromFills(entryFills),
            e.avg_entry_price,
            e.price,
          );

          const qty = eventQtyFromFillsOrEvent(e, 1);
          const targetQty = toFiniteNumber(e.target_qty, qty);

          open = {
            tradeKey: makeTradeKey(s.id, e, enterIndex),
            tradeSeq: e.trade_seq ?? enterIndex,
            t: entryTime,
            price: decisionEntryPrice ?? toFiniteNumber(e.price, 0),
            qty,
            target_qty: targetQty,
            open_qty: qty,
            entry_remaining_qty: toFiniteNumber(e.entry_remaining_qty, 0),
            decision_entry_price: decisionEntryPrice,
            avg_entry_price: avgEntryPrice,
            fills: entryFills,
            broker_order_no: e.broker_order_no ?? null,
            order_status: e.order_status ?? '',
            position_status: e.position_status ?? 'OPEN',
          };
        } else if (e.type === 'exit' && open) {
          const exitFills = normalizeFills(e.fills);
          const avgExitPrice = pickFirstNumber(
            weightedAvgFromFills(exitFills),
            e.avg_exit_price,
            e.price,
          );
          const exitPrice = pickFirstNumber(e.price, avgExitPrice);

          const qty = eventQtyFromFillsOrEvent(
            e,
            open.open_qty ?? open.qty ?? 1,
          );
          const remainingQty = remainingAfterExit(open, e, qty);

          const profit =
            typeof e.profit === 'number'
              ? e.profit
              : (Number(exitPrice) -
                  Number(open.avg_entry_price ?? open.price)) *
                (directionLong ? 1 : -1) *
                qty;

          const exitTime = exitTimeFromFillsOrEvent(e);

          const cumPower = e.cum_power != null ? Number(e.cum_power) : null;
          const maxPower = e.max_power != null ? Number(e.max_power) : null;
          const minPower = e.min_power != null ? Number(e.min_power) : null;

          trades.push({
            tradeKey: open.tradeKey,
            tradeSeq: open.tradeSeq,
            strategyId: s.id,
            directionLabel,
            directionLong,
            qty,
            target_qty: open.target_qty,
            open_qty: remainingQty,
            is_final: e.is_final ?? remainingQty === 0,
            rowKey: `${open.tradeKey}:exit:${e.exit_order_id ?? exitTime}:${exitTime}`,
            entry_remaining_qty: open.entry_remaining_qty,
            position_status:
              e.position_status ?? (remainingQty > 0 ? 'OPEN' : 'CLOSED'),
            broker_order_no: open.broker_order_no,
            order_status: open.order_status,
            entry_price: open.price,
            decision_entry_price: open.decision_entry_price ?? open.price,
            avg_entry_price: open.avg_entry_price,
            stop_loss_price,
            trailing_stop_price,
            entry_timestamp: open.t,
            exit_timestamp: exitTime,
            exit_reason: e.reason ?? '',
            exit_event: e,
            exit_price: exitPrice,
            avg_exit_price: avgExitPrice,
            profit,
            cum_power: cumPower ?? '-',
            max_power: maxPower ?? '-',
            min_power: minPower ?? '-',
            live: false,
          });

          open = remainingQty > 0 ? { ...open, open_qty: remainingQty } : null;
        }
      }

      if (activeSet.has(s.id) && open && latest != null) {
        const activeOpenQty = toFiniteNumber(
          activeRow?.open_qty ?? open.open_qty ?? open.qty,
          open.qty ?? 1,
        );
        const basisForPnl = open.decision_entry_price ?? open.price;
        // LIVE 손익은 보유 수량을 곱하지 않고 1계약 기준으로 표시한다.
        const pnl =
          (latest -
            (activeRow?.open_avg_entry_price ??
              open.avg_entry_price ??
              basisForPnl)) *
          (directionLong ? 1 : -1);

        trades.push({
          tradeKey: open.tradeKey,
          tradeSeq: open.tradeSeq,
          strategyId: s.id,
          directionLabel,
          directionLong,
          qty: activeOpenQty,
          target_qty: toFiniteNumber(
            activeRow?.target_qty ?? open.target_qty,
            activeOpenQty,
          ),
          open_qty: activeOpenQty,
          entry_remaining_qty: toFiniteNumber(
            activeRow?.entry_remaining_qty ?? open.entry_remaining_qty,
            0,
          ),
          position_status:
            activeRow?.position_status ?? open.position_status ?? 'OPEN',
          broker_order_no: open.broker_order_no,
          order_status: open.order_status,
          entry_price: open.price,
          decision_entry_price: open.decision_entry_price ?? open.price,
          avg_entry_price: open.avg_entry_price,
          stop_loss_price,
          trailing_stop_price,
          entry_timestamp: open.t,
          exit_timestamp: null,
          exit_reason: '',
          profit: pnl,
          cum_power: '',
          max_power: '',
          min_power: '',
          live: true,
        });
      }
    }

    return sortTradesByExitTime(trades);
  }

  // ─── 집계(세션 1일 반영) ────────────────────────────
  function calcAggregates(trades, ranges, sessionDayRange) {
    const inRange = (ts, { start, end }) => {
      const t = new Date(ts);
      return t >= start && t <= end;
    };
    const realizedLegs = trades.filter((t) => !t.live && t.exit_timestamp);
    const closed = closedTradeCycles(trades);

    const summarize = (range) => {
      const closedIn = closed.filter((t) => inRange(t.exit_timestamp, range));
      // 총 수익은 전체(본절 포함) 합산 유지
      const profitsAll = realizedLegs
        .filter((t) => inRange(t.exit_timestamp, range))
        .map((t) => Number(t.profit) || 0);

      // break_even(본절 청산)은 무승부(draw)로 집계하고 승/패/승률에서는 제외
      const draws = closedIn.filter(
        (t) => isBreakEvenExit(t.exit_reason) && !t.hasPartialExits,
      ).length;
      const nonDraw = closedIn.filter(
        (t) => !(isBreakEvenExit(t.exit_reason) && !t.hasPartialExits),
      );
      const profits = nonDraw.map((t) => Number(t.profit) || 0);

      const wins = profits.filter((p) => p > 0).length;
      const losses = profits.filter((p) => p < 0).length;

      const denom = wins + losses; // draw 및 0포인트는 승/패 제외
      const winRate = denom ? (wins / denom) * 100 : 0;

      const profitSum = profitsAll.reduce((a, b) => a + b, 0);

      return { wins, losses, draws, winRate, profit: profitSum };
    };

    // 1일(세션 기준): entries는 '세션에 진입한 거래 수', 승/패/수익은 '세션에 청산된 거래' 기준
    const dayStats = summarize(sessionDayRange);
    const oneDayEntries = countEntries(
      trades.filter((t) => inRange(t.entry_timestamp, sessionDayRange)),
    );

    // 나머지 기간(캘린더): entries는 entry 기준, 승/패/수익은 exit 기준
    const weekStats = summarize(ranges.week);
    const weekEntries = countEntries(
      trades.filter((t) => inRange(t.entry_timestamp, ranges.week)),
    );

    const monthStats = summarize(ranges.month);
    const monthEntries = countEntries(
      trades.filter((t) => inRange(t.entry_timestamp, ranges.month)),
    );

    const threeStats = summarize(ranges.threeMonths);
    const threeEntries = countEntries(
      trades.filter((t) => inRange(t.entry_timestamp, ranges.threeMonths)),
    );

    const sixStats = summarize(ranges.sixMonths);
    const sixEntries = countEntries(
      trades.filter((t) => inRange(t.entry_timestamp, ranges.sixMonths)),
    );

    const twelveStats = summarize(ranges.twelveMonths);
    const twelveEntries = countEntries(
      trades.filter((t) => inRange(t.entry_timestamp, ranges.twelveMonths)),
    );

    return [
      {
        label: '1일',
        entries: oneDayEntries,
        profit: dayStats.profit,
        wins: dayStats.wins,
        draws: dayStats.draws,
        losses: dayStats.losses,
        winRate: dayStats.winRate,
      },
      {
        label: '1주',
        entries: weekEntries,
        profit: weekStats.profit,
        wins: weekStats.wins,
        draws: weekStats.draws,
        losses: weekStats.losses,
        winRate: weekStats.winRate,
      },
      {
        label: '1개월',
        entries: monthEntries,
        profit: monthStats.profit,
        wins: monthStats.wins,
        draws: monthStats.draws,
        losses: monthStats.losses,
        winRate: monthStats.winRate,
      },
      {
        label: '3개월',
        entries: threeEntries,
        profit: threeStats.profit,
        wins: threeStats.wins,
        draws: threeStats.draws,
        losses: threeStats.losses,
        winRate: threeStats.winRate,
      },
      {
        label: '6개월',
        entries: sixEntries,
        profit: sixStats.profit,
        wins: sixStats.wins,
        draws: sixStats.draws,
        losses: sixStats.losses,
        winRate: sixStats.winRate,
      },
      {
        label: '12개월',
        entries: twelveEntries,
        profit: twelveStats.profit,
        wins: twelveStats.wins,
        draws: twelveStats.draws,
        losses: twelveStats.losses,
        winRate: twelveStats.winRate,
      },
    ];
  }

  const trades = useMemo(
    () => buildTradesFromEvents(history, latestClose, active),
    [history, latestClose, active],
  );

  // LIVE 포지션(현재 진입 중인 strategy)에 대한 진입/손절/60% 가격만 따로 추출
  const liveTradesForPanel = useMemo(() => {
    if (!Array.isArray(history) || !Array.isArray(active)) return [];

    const liveActive = active.filter((s) => s.is_enter);

    const activeSet = new Set(liveActive.map((s) => s.id));
    const activeMap = new Map(liveActive.map((s) => [s.id, s]));

    const result = [];

    for (const s of history) {
      if (!activeSet.has(s.id)) continue;

      const a = activeMap.get(s.id);
      const directionLong = s.direction === 'long' || s.direction === true;

      const stopLoss =
        (a?.stop_loss_price ?? s.stop_loss_price) != null
          ? Number(a?.stop_loss_price ?? s.stop_loss_price)
          : null;

      const trailing =
        (a?.trailing_stop_price ?? s.trailing_stop_price) != null
          ? Number(a?.trailing_stop_price ?? s.trailing_stop_price)
          : null;

      // 중요:
      // events_history는 append 순서를 그대로 사용한다.
      // t 기준 sort 금지. 기존 이벤트의 t가 잘못 저장된 경우 순서가 깨진다.
      const evts = Array.isArray(s.events_history) ? [...s.events_history] : [];

      let open = null;
      let enterIndex = 0;

      for (const e of evts) {
        if (e.type === 'enter') {
          enterIndex += 1;

          const entryFills = normalizeFills(e.fills);
          const entryTime = eventTimeFromFillsOrEvent(e) || e.t;

          const decisionEntryPrice = pickFirstNumber(
            e.decision_entry_price,
            e.real_entry_price,
            e.entry_price,
            e.price,
            decisionEntryPriceFromFills(directionLong, entryFills),
          );

          const avgEntryPrice = pickFirstNumber(
            weightedAvgFromFills(entryFills),
            e.avg_entry_price,
            e.price,
            decisionEntryPrice,
          );

          const qty = eventQtyFromFillsOrEvent(e, 1);
          const targetQty = toFiniteNumber(e.target_qty, qty);

          open = {
            tradeKey: makeTradeKey(s.id, e, enterIndex),
            tradeSeq: e.trade_seq ?? enterIndex,
            t: entryTime,
            price: decisionEntryPrice ?? Number(e.price),
            decision_entry_price: decisionEntryPrice,
            avg_entry_price: avgEntryPrice,
            qty,
            target_qty: targetQty,
            open_qty: qty,
            entry_remaining_qty: toFiniteNumber(e.entry_remaining_qty, 0),
            position_status: e.position_status ?? 'OPEN',
            broker_order_no: e.broker_order_no ?? null,
            order_status: e.order_status ?? '',
          };
        } else if (e.type === 'exit' && open) {
          const qty = eventQtyFromFillsOrEvent(e, open.open_qty ?? open.qty);
          const remainingQty = remainingAfterExit(open, e, qty);
          open = remainingQty > 0 ? { ...open, open_qty: remainingQty } : null;
        }
      }

      if (open) {
        const activeOpenQty = toFiniteNumber(
          a?.open_qty ?? open.open_qty ?? open.qty,
          open.qty ?? 1,
        );

        result.push({
          tradeKey: open.tradeKey,
          tradeSeq: open.tradeSeq,
          strategyId: s.id,

          qty: activeOpenQty,
          target_qty: toFiniteNumber(
            a?.target_qty ?? open.target_qty,
            activeOpenQty,
          ),
          open_qty: activeOpenQty,

          position_status: a?.position_status ?? open.position_status ?? 'OPEN',

          entry_price: open.entry_price ?? open.price,
          decision_entry_price: open.decision_entry_price ?? open.price,
          avg_entry_price: open.avg_entry_price,

          // 중요:
          // active row의 entry_timestamp보다 open.t를 우선한다.
          // 과거 데이터/재진입 데이터에서 active row가 꼬일 수 있기 때문이다.
          entry_timestamp: open.t,

          stop_loss_price: stopLoss,
          trailing_stop_price: trailing,

          broker_order_no: open.broker_order_no,
          order_status: open.order_status,
        });
      }
    }

    return result;
  }, [history, active]);

  // 당일(=현재 세션) 거래 내역
  const todayTrades = useMemo(() => {
    const { startKst, endKst } = getCurrentSessionWindowKST();
    return trades.filter((t) => {
      const et = new Date(t.entry_timestamp);
      const xt = t.exit_timestamp ? new Date(t.exit_timestamp) : null;
      return (
        (et >= startKst && et <= endKst) ||
        (xt && xt >= startKst && xt <= endKst)
      );
    });
  }, [trades]);

  // 누적 수익 – 1일만 세션 기준으로 교체
  const aggregates = useMemo(() => {
    const { startKst, endKst } = getCurrentSessionWindowKST();
    const sessionDayRange = { start: startKst, end: endKst };
    return calcAggregates(trades, ranges, sessionDayRange);
  }, [trades, ranges]);

  // 수동 새로고침(히스토리만)
  const refreshHistory = async () => {
    try {
      const h = await fetchHistory();
      if (h) {
        setHistory(h);
        historyFpRef.current = calcHistoryFingerprint(h);
      }
    } catch (e) {
      if (!isCanceled(e)) console.error(e);
    }
  };

  return (
    <div className={`pb-4 space-y-4 ${className}`}>
      <div className='flex items-center justify-between'>
        <h2 className='text-xl font-bold'>자동 거래 내역 통계</h2>
        <div className='flex items-center text-sm'>
          <button
            onClick={refreshHistory}
            className='rounded-md border px-3 py-1 hover:bg-gray-50'
            disabled={loadingHistory}
            title='히스토리 새로고침'
          >
            {loadingHistory ? '갱신중...' : '통계 새로고침'}
          </button>
        </div>
      </div>

      {/* 진입,손절,트레일링 가격,강도 표시(LIVE) */}
      <section>
        <div className='rounded-lg border border-gray-200'>
          <table className='w-full min-w-[360px] text-sm'>
            <thead className='bg-gray-200 border-b sticky top-0 z-10'>
              <tr>
                <th className='px-3 py-2 text-right align-bottom'>
                  <div className='leading-tight'>
                    <div className='font-semibold py-2'>진입 기준가</div>
                  </div>
                  <div className='leading-tight'>
                    <div className='font-semibold py-2'>현재 강도</div>
                  </div>
                </th>
                <th className='px-3 py-2 text-right align-bottom'>
                  <div className='leading-tight'>
                    <div className='font-semibold py-2'>손절 예정가</div>
                  </div>
                  <div className='leading-tight'>
                    <div className='font-semibold py-2'>최고 강도</div>
                  </div>
                </th>
                <th className='px-3 py-2 text-right align-bottom'>
                  <div className='leading-tight'>
                    <div className='font-semibold py-2'>트레일링 익절가</div>
                  </div>
                  <div className='leading-tight'>
                    <div className='font-semibold py-2'>최저 강도</div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    className='px-3 py-3 text-gray-400 text-center'
                    colSpan={3}
                  >
                    로딩 중…
                  </td>
                </tr>
              ) : liveTradesForPanel.length === 0 ? (
                <tr>
                  <td
                    className='px-3 py-3 text-gray-400 text-center'
                    colSpan={3}
                  >
                    현재 진행 중인 포지션이 없습니다.
                  </td>
                </tr>
              ) : (
                liveTradesForPanel.map((t) => {
                  const p = livePowerStats[String(t.strategyId)];
                  return (
                    <React.Fragment key={t.tradeKey || t.strategyId}>
                      {/* 1행 */}
                      <tr className='bg-yellow-50'>
                        <td className='px-3 py-2 text-right'>
                          {t.entry_price != null
                            ? fmtPrice(t.entry_price)
                            : '—'}
                        </td>
                        <td className='px-3 py-2 text-right'>
                          {t.stop_loss_price != null
                            ? fmtPrice(t.stop_loss_price)
                            : '—'}
                        </td>
                        <td className='px-3 py-2 text-right'>
                          {t.trailing_stop_price != null
                            ? fmtPrice(t.trailing_stop_price)
                            : '—'}
                        </td>
                      </tr>
                      {/* 2행 */}
                      <tr className='bg-yellow-50 border-b'>
                        <td className='px-3 py-2 text-right'>
                          {p ? fmtPower(p.cum) : '—'}
                        </td>
                        <td className='px-3 py-2 text-right'>
                          {p ? fmtPower(p.max) : '—'}
                        </td>
                        <td className='px-3 py-2 text-right'>
                          {p ? fmtPower(p.min) : '—'}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* 당일 거래 내역(세션 기준) */}
      <section>
        <h2 className='text-lg font-semibold mb-3'>당일 거래 내역</h2>
        <div className='overflow-x-auto rounded-lg border border-gray-200'>
          <div className='max-h-[calc(330px)] overflow-y-auto'>
            <table className='w-full min-w-[360px] text-sm'>
              <thead className='bg-gray-200 border-b sticky top-0 z-10'>
                <tr>
                  <th className='px-3 py-2 text-left align-bottom'>
                    <div className='leading-tight'>
                      <div className='font-semibold'>매수/매도</div>
                      <div className='font-semibold py-2'>청산 사유</div>
                      <div className='font-semibold'>청산 강도</div>
                    </div>
                  </th>
                  <th className='px-3 py-2 text-right align-bottom'>
                    <div className='leading-tight'>
                      <div className='font-semibold'>대표 진입가</div>
                      <div className='font-semibold py-2'>평균 청산가</div>
                      <div className='font-semibold'>최대 강도</div>
                    </div>
                  </th>
                  <th className='px-3 py-2 text-left align-bottom'>
                    <div className='leading-tight'>
                      <div className='font-semibold'>진입 시점</div>
                      <div className='font-semibold py-2'>청산 시점</div>
                      <div className='font-semibold'>최저 강도</div>
                    </div>
                  </th>
                  <th className='px-3 py-2 text-right align-bottom'>
                    <div className='leading-tight'>
                      <div className='font-semibold'>보유 시간</div>
                      <div className='font-semibold py-2'>실제 총손익</div>
                      <div className='font-semibold'>수량</div>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {(loading && (
                  <tr>
                    <td className='px-3 py-3 text-gray-400' colSpan={4}>
                      로딩 중…
                    </td>
                  </tr>
                )) ||
                  (todayTrades.length === 0 && (
                    <tr>
                      <td className='px-3 py-3 text-gray-400' colSpan={4}>
                        당일 거래 내역이 없습니다.
                      </td>
                    </tr>
                  )) ||
                  todayTrades.map((t, idx) => {
                    const live = t.live;
                    const rowBg = live
                      ? 'bg-yellow-50'
                      : idx % 2 === 0
                        ? 'bg-white'
                        : 'bg-gray-50';
                    const profitCls =
                      Number(t.profit) >= 0
                        ? 'text-emerald-600'
                        : 'text-red-600';
                    const directionColor = t.directionLong
                      ? 'text-red-600 px-3 py-2 font-semibold'
                      : 'text-blue-600 px-3 py-2 font-semibold';

                    const holdingText =
                      t.exit_timestamp && !live
                        ? fmtDuration(
                            new Date(t.exit_timestamp) -
                              new Date(t.entry_timestamp),
                          )
                        : '—';

                    const p = live
                      ? livePowerStats[String(t.strategyId)]
                      : null;

                    return (
                      <React.Fragment
                        key={
                          t.rowKey ||
                          t.tradeKey ||
                          `${t.strategyId}-${t.tradeSeq || idx}-${t.entry_timestamp}-${t.exit_timestamp || 'live'}`
                        }
                      >
                        {/* 1행 */}
                        <tr className={rowBg}>
                          <td className={directionColor}>
                            {t.directionLabel}
                            <div className='text-xs text-gray-500 font-normal'>
                              {t.position_status || ''}
                            </div>
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {t.decision_entry_price != null ||
                            t.entry_price != null
                              ? fmtPrice(
                                  t.decision_entry_price ?? t.entry_price,
                                )
                              : ''}
                          </td>
                          <td className='px-3 py-2'>
                            {t.entry_timestamp ? fmtKst(t.entry_timestamp) : ''}
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {holdingText}
                          </td>
                        </tr>

                        {/* 2행 */}
                        <tr className={rowBg}>
                          <td
                            className={
                              live
                                ? 'font-semibold text-red-500 px-3 py-2'
                                : 'px-3 py-2'
                            }
                          >
                            {t.exit_reason
                              ? typeof mapExitReason === 'function'
                                ? mapExitReason(t.exit_reason, t.exit_event)
                                : t.exit_reason
                              : live
                                ? 'LIVE'
                                : ''}
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {t.avg_exit_price != null || t.exit_price != null
                              ? fmtPrice(t.avg_exit_price ?? t.exit_price)
                              : live
                                ? '—'
                                : ''}
                          </td>
                          <td className='px-3 py-2'>
                            {t.exit_timestamp
                              ? fmtKst(t.exit_timestamp)
                              : live
                                ? '—'
                                : ''}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-semibold ${profitCls}`}
                          >
                            {t.profit != null
                              ? Number(t.profit).toFixed(2)
                              : ''}
                          </td>
                        </tr>

                        {/* 3행 */}
                        <tr className={`${rowBg} border-b`}>
                          <td
                            className={
                              live
                                ? 'font-semibold text-red-500 px-3 py-2'
                                : 'px-3 py-2'
                            }
                          >
                            {live
                              ? p
                                ? fmtPower(p.cum)
                                : '—'
                              : (t.cum_power ?? '')}
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {live
                              ? p
                                ? fmtPower(p.max)
                                : '—'
                              : (t.max_power ?? '')}
                          </td>
                          <td className='px-3 py-2'>
                            {live
                              ? p
                                ? fmtPower(p.min)
                                : '—'
                              : (t.min_power ?? '')}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-semibold ${profitCls}`}
                          >
                            {fmtQty(t.qty)} / {fmtQty(t.target_qty)}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 누적수익 + 승률 */}
      <section>
        <h2 className='text-lg font-semibold mb-3'>누적 수익</h2>
        <div className='overflow-x-auto rounded-lg border border-gray-200'>
          <table className='min-w-[360px] text-sm'>
            <thead className='bg-gray-200 border-b'>
              <tr>
                <th className='px-3 py-2 text-left'>기간</th>
                <th className='px-3 py-2 text-right'>총 진입횟수</th>
                <th className='px-3 py-2 text-right'>승/무/패 (승률)</th>
                <th className='px-3 py-2 text-right'>총 수익(포인트)</th>
              </tr>
            </thead>
            <tbody>
              {aggregates.map((row) => (
                <tr key={row.label}>
                  <td className='px-3 py-2'>{row.label}</td>
                  <td className='px-3 py-2 text-right'>{row.entries}</td>
                  <td className='px-3 py-2 text-right'>
                    {row.wins} / {row.draws} / {row.losses}{' '}
                    <span className='text-gray-500'>
                      ({row.winRate.toFixed(1)}%)
                    </span>
                  </td>
                  <td
                    className={`px-3 py-2 text-right ${row.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}
                  >
                    {row.profit.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
